import { useCallback, useEffect, useMemo } from 'react';
import { useRouter } from 'next/router';
import { useQuery } from 'react-query';
import { ordersServices } from '@services';
import { getUrl, showError } from '@utils';
import { getOrdersRequestPayload, getOrdersRequestQueryParams } from '@helpers';
import { LIMIT, ORDERS_STATUS } from '@constants';
import { QUERY_KEYS } from 'constants/queryKeys';
import { URLS } from 'layouts/DrawerLayout/constants';
import { handleOrders } from './utils';

export const ORDERS_PAGE_SIZE = LIMIT;

export const useOrdersOperations = () => {
  const { push, query, isReady } = useRouter();

  const {
    page = 1,
    pageSize = ORDERS_PAGE_SIZE,
    orderBy = 'createdAt',
    order = 'desc',
    status = null,
    token,
    ...others
  } = query;

  const _otherQueries = Object.entries(others)
    .map(([key, value]) => `${key}=${value}`)
    .join('&');

  const ordersResponse = useQuery(
    [QUERY_KEYS.GET_ORDERS_KEY, page, pageSize, orderBy, order, status],
    () => {
      const payload = getOrdersRequestPayload({
        page,
        pageSize: parseInt(pageSize, 10) || ORDERS_PAGE_SIZE,
        orderBy,
        order,
        status,
      });

      return ordersServices?.getOrdersFilters({
        urlParams: getOrdersRequestQueryParams({
          page,
          pageSize: parseInt(pageSize, 10) || ORDERS_PAGE_SIZE,
          orderBy,
          order,
          status,
        }),
        payload,
      });
    },
    { enabled: isReady }
  );

  const orders = handleOrders(ordersResponse?.data?.data || []);

  const _sortBy = useCallback(
    (sortingModel) => {
      let url = `/orders?page=${page}&${_otherQueries}`;
      if (sortingModel?.orderBy || sortingModel?.order) {
        url = `${url}&orderBy=${sortingModel?.orderBy || ''}&order=${
          sortingModel?.order || ''
        }`;
      }
      if (!sortingModel?.orderBy || !sortingModel?.order) {
        url = `/orders?page=${page}&${_otherQueries}`;
      }
      push(url, undefined, { shallow: true });
    },
    [push, page, _otherQueries]
  );

  const handlePageChange = (newPage, newPageSize) => {
    const currentPage = query.page || '1';
    const currentPageSize = query.pageSize || ORDERS_PAGE_SIZE.toString();

    const urlParams = { ...query };

    if (newPage !== currentPage) {
      urlParams.page = newPage;
    }

    if (newPageSize && newPageSize.toString() !== currentPageSize) {
      urlParams.pageSize = newPageSize;
      urlParams.page = 1;
    }

    push(getUrl(urlParams, URLS.orders));
  };

  useEffect(() => {
    if (ordersResponse?.error) {
      showError(ordersResponse?.error);
    }
  }, [ordersResponse?.error]);

  const statuses = useMemo(() => {
    return Object.keys(ORDERS_STATUS)?.map((key) => {
      return { text: ORDERS_STATUS[key], value: key };
    });
  }, []);

  return {
    page,
    statuses,
    total: ordersResponse?.data?.meta?.total,
    loading: ordersResponse?.isLoading,
    orders,
    pageSize: parseInt(pageSize, 10) || ORDERS_PAGE_SIZE,
    getOrders: ordersResponse.refetch,
    nextPage: handlePageChange,
    sortBy: _sortBy,
  };
};
